<template>
  <b-card title="Ajouter un ramasseur">
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
    >
      <validation-observer
        ref="formValidator"
      >
        <b-form @submit.prevent="validateAndSubmit">
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
              />
              Informations personnelles
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Nom du ramasseur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        v-model="formData.username"
                        :state="errors.length > 0 || serverErrors.username ? false : (valid && formData.username ? true : null)"
                        placeholder="Entrez le nom complet"
                        class="premium-input"
                        @input="clearServerError('username')"
                      />
                      <feather-icon
                        v-if="valid && formData.username && !serverErrors.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0] || serverErrors.username"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] || serverErrors.username }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Email <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Email"
                    rules="required|email|asyncEmail"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="formData.email"
                        type="email"
                        :state="errors.length > 0 || serverErrors.email ? false : (valid && formData.email ? true : null)"
                        placeholder="Entrez l'email"
                        class="premium-input"
                        @input="clearServerError('email')"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="RefreshCwIcon"
                          :class="formData.username && formData.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          v-b-tooltip.hover="emailGenerationTooltip"
                          @click="formData.username && formData.username.trim().length > 0 ? generateCompanyEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0] || serverErrors.email"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] || serverErrors.email }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Téléphone <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Téléphone"
                    rules="required|tnPhone|asyncPhone"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="formData.phone"
                        :formatter="formatPhone"
                        @keypress="allowOnlyNumbers"
                        :state="errors.length > 0 || serverErrors.phone ? false : (valid && formData.phone ? true : null)"
                        placeholder="Entrez le numéro de téléphone"
                        maxlength="8"
                        class="premium-input"
                        @input="clearServerError('phone')"
                      />
                      <feather-icon
                        v-if="valid && formData.phone && !serverErrors.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0] || serverErrors.phone"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] || serverErrors.phone }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone 2 -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Téléphone secondaire (optionnel)">
                  <b-form-input
                    id="phone2"
                    v-model="formData.phone2"
                    :formatter="formatPhone"
                    @keypress="allowOnlyNumbers"
                    placeholder="Entrez le numéro de téléphone secondaire"
                    maxlength="8"
                    class="premium-input"
                    :state="serverErrors.phone2 ? false : null"
                    @input="clearServerError('phone2')"
                  />
                  <small
                    v-if="serverErrors.phone2"
                    class="text-danger validation-message-animate"
                  >{{ serverErrors.phone2 }}</small>
                </b-form-group>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Adresse">
                  <b-form-textarea
                    v-model="formData.address"
                    placeholder="Entrez l'adresse"
                    rows="3"
                    class="premium-input"
                  />
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Véhicule">
                  <div class="d-flex align-items-center">
                    <v-select
                      id="car"
                      v-model="formData.car"
                      :options="carOptions"
                      label="displayName"
                      :reduce="car => car.id"
                      placeholder="Sélectionnez un véhicule (optionnel)"
                      clearable
                      class="enhanced-select border-select flex-grow-1 mr-1"
                    />
                    <b-button
                      variant="outline-primary"
                      class="btn-icon"
                      v-b-tooltip.hover="'Créer un véhicule rapidement'"
                      @click="$refs.quickCreateCarModal.show()"
                    >
                      <feather-icon icon="ZapIcon" />
                    </b-button>
                  </div>
                </b-form-group>
              </b-col>

              <!-- Repository -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Dépôt">
                  <v-select
                    id="repository"
                    v-model="formData.repository"
                    :options="repositories"
                    label="name"
                    :reduce="repo => repo.id"
                    placeholder="Sélectionnez un dépôt (optionnel)"
                    clearable
                    class="enhanced-select border-select"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <h5 class="mt-3 mb-3">
              <feather-icon
                icon="LockIcon"
                class="mr-1"
              />
              Informations d'accès
            </h5>

            <b-row>
              <!-- Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          v-model="formData.password"
                          :type="showPassword ? 'text' : 'password'"
                          :state="errors.length > 0 || serverErrors.password ? false : (valid && formData.password ? true : null)"
                          placeholder="Entrez le mot de passe"
                          class="premium-input"
                          @input="clearServerError('password')"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            class="cursor-pointer"
                            @click="showPassword = !showPassword"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <feather-icon
                        v-if="valid && formData.password && !serverErrors.password"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                        style="right: 3.5rem;"
                      />
                    </div>
                    <small
                      v-if="errors[0] || serverErrors.password"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] || serverErrors.password }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Confirmer le mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          v-model="formData.confirm_password"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          :state="errors.length > 0 || serverErrors.confirm_password ? false : (valid && formData.confirm_password ? true : null)"
                          placeholder="Confirmez le mot de passe"
                          class="premium-input"
                          @input="clearServerError('confirm_password')"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            class="cursor-pointer"
                            @click="showConfirmPassword = !showConfirmPassword"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <feather-icon
                        v-if="valid && formData.confirm_password && !serverErrors.confirm_password"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                        style="right: 3.5rem;"
                      />
                    </div>
                    <small
                      v-if="errors[0] || serverErrors.confirm_password"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] || serverErrors.confirm_password }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-3 mt-2">
              <PasswordGenerator
                :form-data="formData"
                user-type="pickupeur"
                @generated="onPasswordGenerated"
              />
            </div>

            <div class="d-flex justify-content-between mt-3">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'pickupeur-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                Annuler
              </b-button>

              <span id="pickupeur-create-save-btn-wrapper" class="d-inline-block">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="!isFormValid || isSubmitting"
                  :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                  />
                  Enregistrer
                </b-button>
              </span>
              <b-tooltip
                v-if="!isFormValid || isSubmitting"
                target="pickupeur-create-save-btn-wrapper"
                triggers="hover"
                placement="top"
              >
                {{ tooltipMessage }}
              </b-tooltip>
            </div>
          </b-card>
        </b-form>
      </validation-observer>
    </b-overlay>
    <QuickCreateCarModal
      ref="quickCreateCarModal"
      @car-created="handleCarCreated"
    />
  </b-card>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, email, numeric, length, min, confirmed,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  BOverlay,
  BFormTextarea,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import QuickCreateCarModal from '@/components/modals/QuickCreateCarModal.vue'
import {
  createAsyncEmailValidator,
  createAsyncPhoneValidator,
} from '@/utils/asyncValidators'
import '@core/utils/validations/validations'
import axios from 'axios'

extend('required', required)
extend('email', email)
extend('numeric', numeric)
extend('length', length)
extend('min', min)
extend('confirmed', confirmed)
extend('asyncEmail', {
  validate: async (value, { excludeUserId }) => {
    const validator = createAsyncEmailValidator(excludeUserId || null)
    return validator(value)
  },
  params: ['excludeUserId'],
  message: 'Cet email est déjà utilisé.',
})
extend('asyncPhone', {
  validate: async (value, { companyId, excludeUserId }) => {
    const validator = createAsyncPhoneValidator(companyId || null, excludeUserId || null)
    return validator(value)
  },
  params: ['companyId', 'excludeUserId'],
  message: 'Ce numéro de téléphone est déjà utilisé.',
})

export default {
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    QuickCreateCarModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BOverlay,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      cars: [],
      repositories: [],
      showPassword: false,
      showConfirmPassword: false,
      serverErrors: {},
      formData: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        car: null,
        repository: null,
        password: '',
        confirm_password: '',
      },
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.formData.username && this.formData.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du ramasseur'
    },

    carOptions() {
      if (!Array.isArray(this.cars)) return []
      return this.cars.map(car => ({
        ...car,
        displayName: `${car.brand || 'N/A'} - ${car.serial_number || 'N/A'} (${car.color || 'N/A'})`,
      }))
    },

    isPhoneValid() {
      if (!this.formData.phone) return false
      const cleaned = String(this.formData.phone).replace(/\D/g, '')
      return cleaned.length === 8 && /^[24579]\d{7}$/.test(cleaned)
    },

    isFormValid() {
      return (
        this.formData.username !== ''
        && this.formData.username.trim().length > 0
        && this.formData.email !== ''
        && this.formData.phone !== ''
        && this.formData.password !== ''
        && this.formData.password.length >= 8
        && this.formData.confirm_password !== ''
        && this.formData.password === this.formData.confirm_password
      )
    },

    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      const missing = []
      if (!this.formData.username || !this.formData.username.trim()) missing.push('Nom')
      if (!this.formData.email || !this.formData.email.trim()) missing.push('Email')
      if (!this.formData.phone || !this.formData.phone.trim()) missing.push('Téléphone')
      if (!this.formData.password || this.formData.password.length < 8) missing.push('Mot de passe')
      if (!this.formData.confirm_password || this.formData.password !== this.formData.confirm_password) missing.push('Confirmation mot de passe')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchCars(), this.fetchRepositories()])
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    clearServerError(field) {
      if (this.serverErrors[field]) {
        this.$delete(this.serverErrors, field)
      }
    },

    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      if (target && target.id === 'phone' && (this.formData?.phone || this.phone)?.length >= 8) {
        event.preventDefault()
      }
      if (target && target.id === 'phone2' && (this.formData?.phone2 || this.phone2)?.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    handleCarCreated(newCar) {
      if (newCar && newCar.id) {
        this.cars.push(newCar)
        this.formData.car = newCar.id
        this.showToast('success', `Véhicule ${newCar.serial_number || ''} créé et sélectionné`)
      }
    },

    async fetchRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        if (Array.isArray(response.data)) {
          this.repositories = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          this.repositories = response.data.results
        }
      } catch (error) {
        this.repositories = []
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/')
        let cars = []
        if (Array.isArray(response.data)) {
          cars = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          cars = response.data.results
        }
        this.cars = cars
      } catch (error) {
        this.cars = []
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    normalizeErrorMessage(value) {
      if (Array.isArray(value)) {
        return this.normalizeErrorMessage(value[0])
      }
      if (value && typeof value === 'object') {
        const firstKey = Object.keys(value)[0]
        return firstKey ? this.normalizeErrorMessage(value[firstKey]) : ''
      }
      if (typeof value !== 'string') return ''
      return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim()
    },

    extractPickupeurError(error) {
      const fallbackMessage = 'Erreur lors de la création du ramasseur'
      const fieldLabels = {
        username: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        phone2: 'Téléphone secondaire',
        repository: 'Dépôt',
        car: 'Véhicule',
        password: 'Mot de passe',
        confirm_password: 'Confirmation mot de passe',
      }

      const errorData = error && error.response ? error.response.data : null

      if (typeof errorData === 'string') {
        if (errorData.includes("Ce nom d'utilisateur est déjà utilisé") || errorData.includes('username already exists')) {
          return {
            message: "Ce nom d'utilisateur est déjà utilisé.",
            fieldErrors: { username: "Ce nom d'utilisateur est déjà utilisé." },
          }
        }

        return {
          message: this.normalizeErrorMessage(errorData) || fallbackMessage,
          fieldErrors: {},
        }
      }

      if (!errorData || typeof errorData !== 'object') {
        return {
          message: fallbackMessage,
          fieldErrors: {},
        }
      }

      const fieldsToProcess = (errorData.errors && typeof errorData.errors === 'object')
        ? errorData.errors
        : errorData

      const fieldErrors = {}
      const messages = []

      Object.keys(fieldsToProcess).forEach(field => {
        const message = this.normalizeErrorMessage(fieldsToProcess[field])
        if (!message) return

        fieldErrors[field] = message

        if (message.includes('existe') || message.includes('déjà utilisé') || message.includes('already exists')) {
          if (field === 'phone') messages.push('Ce numéro de téléphone est déjà utilisé.')
          else if (field === 'email') messages.push('Cette adresse email est déjà utilisée.')
          else if (field === 'username') messages.push("Ce nom d'utilisateur est déjà utilisé.")
          else messages.push(`Le champ ${fieldLabels[field] || field} est déjà utilisé.`)
        } else {
          messages.push(`${fieldLabels[field] || field}: ${message}`)
        }
      })

      return {
        message: messages.join(' ') || this.normalizeErrorMessage(errorData.message || errorData.detail) || fallbackMessage,
        fieldErrors,
      }
    },

    async validateAndSubmit() {
      const observer = this.$refs.formValidator
      if (observer) {
        const success = await observer.validate()
        if (!success || !this.isFormValid) {
          this.showToast('danger', 'Veuillez corriger les erreurs dans le formulaire')
          return
        }
      } else if (!this.isFormValid) {
        this.showToast('danger', 'Veuillez remplir tous les champs obligatoires')
        return
      }

      await this.submitForm()
    },

    async submitForm() {
      this.isSubmitting = true
      this.serverErrors = {}

      try {
        const payload = {
          username: this.formData.username.trim(),
          email: this.formData.email.trim(),
          phone: this.formData.phone.trim(),
          phone2: this.formData.phone2 ? this.formData.phone2.trim() : '',
          address: this.formData.address ? this.formData.address.trim() : '',
          car: this.formData.car || null,
          repository: this.formData.repository || null,
          password: this.formData.password,
          confirm_password: this.formData.confirm_password,
        }

        await axios.post('/api/auth/register/pickupeur/', payload)

        this.showToast('success', 'Ramasseur ajouté avec succès')
        this.$router.push({ name: 'pickupeur-list' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating pickupeur:', error)

        const { message: errorMessage, fieldErrors } = this.extractPickupeurError(error)
        this.serverErrors = fieldErrors

        this.showToast('danger', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, positionOrText, maybeText) {
      const text = maybeText || positionOrText
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else if (variant === 'info') {
        title = 'Information'
        icon = 'InfoIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant: variant === 'error' ? 'danger' : variant,
        },
      })
    },

    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    async resolveUniqueEmail(emailName, domain) {
      const MAX_ATTEMPTS = 50
      const candidates = []
      for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
        const local = i === 0 ? emailName : `${emailName}.${i}`
        const emailAddress = `${local}@${domain}`
        candidates.push({ email: emailAddress, suffix: i })
      }

      try {
        const { data } = await axios.get('/api/auth/users/pickupeurs/', {
          params: { search: `${emailName}@${domain}`, page_size: MAX_ATTEMPTS + 10 },
        })
        let results = []
        if (data && Array.isArray(data.results)) {
          results = data.results
        } else if (Array.isArray(data)) {
          results = data
        }
        const existingEmails = new Set(
          results
            .map(s => (s.email || '').trim().toLowerCase())
            .filter(Boolean),
        )
        const free = candidates.find(c => !existingEmails.has(c.email.toLowerCase()))
        if (free) {
          return { email: free.email, suffix: free.suffix, skipped: false }
        }
        return { email: candidates[candidates.length - 1].email, suffix: MAX_ATTEMPTS - 1, skipped: true }
      } catch (error) {
        return { email: candidates[0].email, suffix: 0, skipped: true }
      }
    },

    async generateCompanyEmail() {
      const { username } = this.formData
      if (!username || !username.trim()) {
        this.showToast('warning', 'Veuillez d\'abord saisir le nom du ramasseur')
        return
      }

      const emailName = this.stripAccents(username.trim())
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, '.')

      if (!emailName) {
        this.showToast('warning', 'Le nom saisi ne contient aucun caractère utilisable pour générer un email.')
        return
      }

      const domain = this.companyDomain
      if (!domain) {
        this.showToast('danger', 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie')
        return
      }

      const baseEmail = `${emailName}@${domain}`

      try {
        const { email: resolvedEmail, suffix, skipped } = await this.resolveUniqueEmail(emailName, domain)
        this.formData.email = resolvedEmail
        this.clearServerError('email')
        if (skipped) {
          this.showToast('warning', `Email généré : ${resolvedEmail} (unicité non vérifiée — vérifié à l'enregistrement)`)
        } else if (suffix > 0) {
          this.showToast('success', `Email généré : ${resolvedEmail} (suffixe .${suffix} ajouté pour éviter un doublon)`)
        } else {
          this.showToast('success', `Email généré avec succès (${resolvedEmail})`)
        }
      } catch {
        this.formData.email = baseEmail
        this.clearServerError('email')
        this.showToast('warning', `Email généré : ${baseEmail} (unicité non vérifiée — vérifié à l'enregistrement)`)
      }
    },

    onPasswordGenerated(generatedPassword) {
      this.formData.password = generatedPassword
      this.formData.confirm_password = generatedPassword
      this.clearServerError('password')
      this.clearServerError('confirm_password')
      this.showPassword = true
      this.showConfirmPassword = true
      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #C62828;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

/* Generate Password Button Styling */
.generate-password-btn-centered {
  padding: 0.75rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9375rem;
  border: 2px solid #C62828;
  color: #C62828;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

  &:hover {
    background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    border-color: #C62828;
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
